import React, {useRef, useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Link,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useIntl} from 'react-intl'

import {generateStyles} from '../../utils/utils'
import {localizeSeoHtmlLinks} from '../../../../utils/url'

const SeoFooterStyleBlock = ({
    seoContent,
    categoryName,
    seoFooterStyleBlockContainerStyle,
    SeeLessText,
    SeeMoreText,
    SeeMoreLessTextStyle,
    SeoFooterStyleBlockTitleStyle,
    SeoFooterStyleBlockTextStyle
}) => {
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const appOrigin = useAppOrigin()
    const styles = useMultiStyleConfig('SeoFooterStyleBlock')
    const localizedSeoContent = useMemo(
        () => localizeSeoHtmlLinks(seoContent, {buildUrl, appOrigin}),
        [seoContent, buildUrl, appOrigin]
    )
    const NO_OF_LINES = 3
    const contentRef = useRef(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [shouldShowReadMore, setShouldShowReadMore] = useState(false)
    const [fullHeight, setFullHeight] = useState(null)
    const [collapsedHeight, setCollapsedHeight] = useState(null)

    useEffect(() => {
        if (contentRef.current) {
            const offset = 2
            const lineHeight = parseInt(
                window.getComputedStyle(contentRef.current).lineHeight,
                10
            )
            const threeLineHeight = lineHeight * NO_OF_LINES + offset
            const scrollHeight = contentRef.current.scrollHeight + offset

            setFullHeight(scrollHeight)
            setCollapsedHeight(threeLineHeight)
            setShouldShowReadMore(scrollHeight > threeLineHeight)
        }
    }, [localizedSeoContent])

    const handleToggleReadMore = (e) => {
        if (e) e.preventDefault()
        setIsExpanded(!isExpanded)
    }

    if (!seoContent) {
        return null
    }

    return (
        <Box
            {...styles.container}
            {...seoFooterStyleBlockContainerStyle}
            data-block-type="SeoFooterStyleBlock"
            data-testid="category-seo-slot"
        >
            <Text {...generateStyles(SeoFooterStyleBlockTitleStyle, {...styles.title})}>
                {categoryName}
            </Text>

            <Box
                overflow="hidden"
                height={
                    fullHeight !== null && collapsedHeight !== null
                        ? isExpanded
                            ? 'auto'
                            : `${collapsedHeight}px`
                        : 'auto'
                }
                transition="height 0.3s ease-in-out"
            >
                <Box
                    ref={contentRef}
                    {...generateStyles(SeoFooterStyleBlockTextStyle, {...styles.content})}
                    dangerouslySetInnerHTML={{__html: localizedSeoContent}}
                />
            </Box>

            {shouldShowReadMore && (
                <Link
                    href="#"
                    onClick={handleToggleReadMore}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            handleToggleReadMore(e)
                        }
                    }}
                    {...generateStyles(SeeMoreLessTextStyle, {...styles?.showHideCta})}
                >
                    {isExpanded
                        ? SeeLessText ||
                          intl.formatMessage({
                              id: 'plp.seo.read_less',
                              defaultMessage: 'Read Less'
                          })
                        : SeeMoreText ||
                          intl.formatMessage({
                              id: 'plp.seo.read_more',
                              defaultMessage: 'Read More'
                          })}
                </Link>
            )}
        </Box>
    )
}

SeoFooterStyleBlock.propTypes = {
    seoContent: PropTypes.string,
    categoryName: PropTypes.string,
    seoFooterStyleBlockContainerStyle: PropTypes.object,
    SeeLessText: PropTypes.string,
    SeeMoreText: PropTypes.string,
    SeeMoreLessTextStyle: PropTypes.object,
    SeoFooterStyleBlockTitleStyle: PropTypes.object,
    SeoFooterStyleBlockTextStyle: PropTypes.object
}

export default SeoFooterStyleBlock
